import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Product } from '@/mocks/products';

const BADGE_KEYS: Record<string, string> = {
  'Mais Vendido': 'badge_best_seller',
  'Novo': 'badge_new',
  'Premium': 'badge_premium',
  'Cask Strength': 'badge_cask_strength',
  'Clássico': 'badge_classic',
  'Exclusivo': 'badge_exclusive',
  'Edição Limitada': 'badge_limited',
  'Navy Strength': 'badge_navy',
  'Importado': 'badge_imported',
};

const DESC_EN: Record<string, string> = {
  'ardmore-islay-single-malt': 'Distilled on the shores of Islay, the Ardmore carries the raw and poetic essence of the island. Notes of intense peat mingle with sea smoke, wild honey and a touch of vanilla. Aged for 12 years in American oak casks, it is a whisky of unmistakable character. Every sip is a journey to the Atlantic coasts of Scotland — where wind, peat and sea merge into something truly unique.',
  'glen-speyside-12': 'Born in the heart of the Spey Valley, this single malt captures the floral and fruity essence that made Speyside famous worldwide. Aged in European and American oak casks, it develops a delicate complexity — green apple, ripe pear, flower honey and a touch of gentle spices that linger in the elegant finish.',
  'highland-reserve-18': 'Aged for 18 years in Oloroso sherry casks, the Highland Reserve is an expression of rare complexity. Notes of sherry, raisins and dark chocolate intertwine with oriental spices and a deep oak. A whisky for special moments — to be savoured slowly, with attention and reverence.',
  'blended-malt-reserve': 'A careful selection of malts from different regions of Scotland, aged for a minimum of 15 years. The art of blending lies in finding the perfect balance — and here it has been achieved with mastery.',
  'peat-monster-cask': 'Bottled straight from the cask without dilution, the Peat Monster Cask is a declaration of intensity. Extreme peat, coal smoke, sea salt and a warmth that warms the soul. For true Islay lovers — no concessions, no filters, pure essence.',
  'lowland-gentle-spirit': 'The Lowlands produce whiskies of soft and elegant character, ideal for those discovering the world of single malts. Floral, lightly citrusy and with a clean finish — a perfect introduction to the complexity of Scottish spirits.',
  'botanica-london-dry': 'Distilled in copper pot stills in small batches, the Botânica London Dry is the purest expression of the London style. Fresh juniper, Sicilian lemon peel, coriander and angelica root create a gin of impeccable balance — classic, clean and versatile.',
  'floral-botanical-gin': 'Inspired by the botanical gardens of the Scottish Highlands, the Floral Botanical is an ode to nature in distilled form. Rose, lavender, hibiscus and violet petals create a gin of rare delicacy — fragrant, elegant and unforgettable.',
  'small-batch-spiced-gin': 'Produced in batches of only 500 bottles, the Small Batch Spiced is for those seeking intensity and personality. Pink pepper, cardamom, fresh ginger and juniper create a bold and memorable experience — a gin that makes no apologies.',
  'mediterranean-dry-gin': 'Inspired by the aromatic herbs and citrus fruits of the Mediterranean, this gin captures the essence of southern Europe. Seville orange, thyme, rosemary and juniper create a vibrant, refreshing and characterful gin.',
  'navy-strength-gin': 'Inspired by the historic gins of the Royal Navy, the Navy Strength is bottled at 57% — the minimum strength to not extinguish gunpowder. Intense juniper, spices and a strength that impresses. For those who are not afraid of intensity.',
  'japanese-yuzu-gin': 'Distilled in Japan with local botanicals, the Japanese Yuzu is a unique fusion between gin tradition and Japanese aesthetics. Yuzu, green tea, sakura blossom and juniper create a gin of rare elegance — delicate, fragrant and unforgettable.',
};

interface ProductInfoProps {
  product: Product;
}

const ProductInfo = ({ product }: ProductInfoProps) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [wishlist, setWishlist] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const handleAddToCart = () => {
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const specs = [
    ...(product.age ? [{ label: t('product_spec_age', 'Idade'), value: product.age, icon: 'ri-time-line' }] : []),
    { label: t('product_spec_abv', 'Teor Alc.'), value: product.abv, icon: 'ri-drop-line' },
    { label: t('product_spec_origin', 'Origem'), value: product.origin, icon: 'ri-map-pin-line' },
    ...(product.style ? [{ label: t('product_spec_style', 'Estilo'), value: product.style, icon: 'ri-star-line' }] : []),
  ];

  return (
    <div className="flex flex-col">
      {/* Category + badge */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[#c9a84c] text-[10px] tracking-[0.35em] uppercase font-semibold">
          {product.category === 'whisky' ? t('product_cat_whisky', 'Whisky Premium') : t('product_cat_gin', 'Gin Artesanal')}
        </span>
        {product.badge && (
          <span className="bg-[#c9a84c]/15 text-[#c9a84c] border border-[#c9a84c]/30 text-[10px] tracking-widest uppercase font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
            {BADGE_KEYS[product.badge] ? t(BADGE_KEYS[product.badge], product.badge) : product.badge}
          </span>
        )}
      </div>

      {/* Name */}
      <h1
        className="text-4xl md:text-5xl font-light text-white mb-1 leading-tight"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {product.name}
      </h1>
      <p className="text-white/40 text-sm mb-6 font-light">{product.subtitle}</p>

      {/* Specs grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {specs.map(spec => (
          <div key={spec.label} className="bg-white/4 border border-white/8 rounded-xl px-4 py-3 flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <i className={`${spec.icon} text-[#c9a84c] text-xs`}></i>
              <p className="text-white/30 text-[10px] tracking-widest uppercase">{spec.label}</p>
            </div>
            <p className="text-white text-sm font-medium">{spec.value}</p>
          </div>
        ))}
      </div>

      {/* Price */}
      <div className="flex items-end gap-4 mb-8 pb-8 border-b border-white/8">
        <div>
          <p className="text-white/30 text-[10px] tracking-widest uppercase mb-1">{t('product_price', 'Preço')}</p>
          <p
            className="text-[#c9a84c] text-4xl font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {product.priceFormatted}
          </p>
        </div>
        <p className="text-white/25 text-xs mb-1.5">{t('product_tax', 'Impostos inclusos')}</p>
      </div>

      {/* Quantity + CTA */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        {/* Qty selector */}
        <div className="flex items-center border border-white/15 rounded-md overflow-hidden">
          <button
            onClick={() => setQty(q => Math.max(1, q - 1))}
            className="w-11 h-12 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className="w-12 text-center text-white text-sm font-medium">{qty}</span>
          <button
            onClick={() => setQty(q => q + 1)}
            className="w-11 h-12 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>

        {/* Add to cart */}
        <button
          onClick={handleAddToCart}
          className={`flex-1 text-xs tracking-[0.2em] uppercase font-bold px-8 py-3.5 rounded-md transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
            added
              ? 'bg-green-600 text-white'
              : 'bg-[#c9a84c] hover:bg-[#b8963e] text-black'
          }`}
        >
          {added ? (
            <><i className="ri-check-line text-base"></i>{t('product_added', 'Adicionado!')}</>
          ) : (
            <><i className="ri-shopping-bag-line text-base"></i>{t('product_add_cart', 'Adicionar ao Carrinho')}</>
          )}
        </button>

        {/* Wishlist */}
        <button
          onClick={() => setWishlist(w => !w)}
          className={`w-12 h-12 flex items-center justify-center border rounded-md transition-all cursor-pointer ${
            wishlist
              ? 'border-red-500/50 text-red-400 bg-red-500/10'
              : 'border-white/15 text-white/50 hover:border-[#c9a84c]/40 hover:text-[#c9a84c]'
          }`}
        >
          <i className={wishlist ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'}></i>
        </button>
      </div>

      {/* Buy now */}
      <button className="w-full border border-[#c9a84c]/40 hover:bg-[#c9a84c]/8 text-[#c9a84c] text-xs tracking-[0.2em] uppercase font-bold px-8 py-3.5 rounded-md transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mb-8">
        <i className="ri-secure-payment-line text-base"></i>
        {t('product_buy_now', 'Comprar Agora')}
      </button>

      {/* Trust badges */}
      <div className="flex flex-wrap gap-4 mb-8 pb-8 border-b border-white/8">
        {[
          { icon: 'ri-shield-check-line', label: t('product_trust_auth', 'Produto Autêntico') },
          { icon: 'ri-truck-line', label: t('product_trust_ship', 'Entrega Segura') },
          { icon: 'ri-award-line', label: t('product_trust_quality', 'Qualidade Garantida') },
        ].map(item => (
          <div key={item.label} className="flex items-center gap-2 text-white/40">
            <i className={`${item.icon} text-[#c9a84c] text-sm`}></i>
            <span className="text-xs tracking-wide">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Description */}
      <div className="mb-8">
        <h3 className="text-white/60 text-[10px] tracking-[0.3em] uppercase mb-3 font-semibold">
          {t('product_description', 'Descrição')}
        </h3>
        <div className={`relative overflow-hidden transition-all duration-300 ${descExpanded ? '' : 'max-h-24'}`}>
          <p className="text-white/55 text-sm font-light leading-relaxed">
            {isEn ? (DESC_EN[product.slug] ?? product.longDescription) : product.longDescription}
          </p>
          {!descExpanded && (
            <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0a0a0a] to-transparent"></div>
          )}
        </div>
        <button
          onClick={() => setDescExpanded(e => !e)}
          className="mt-2 text-[#c9a84c] text-xs tracking-widest uppercase flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
        >
          {descExpanded ? t('product_read_less', 'Ler menos') : t('product_read_more', 'Ler mais')}
          <i className={`text-xs transition-transform duration-200 ${descExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
        </button>
      </div>

      {/* Tasting notes */}
      <div className="bg-[#111111] border border-white/8 rounded-2xl p-6 mb-8">
        <p className="text-[#c9a84c] text-[10px] tracking-[0.35em] uppercase mb-5 font-semibold">
          {t('product_tasting', 'Notas de Degustação')}
        </p>
        <div className="flex flex-col gap-5">
          {[
            { label: t('product_nose', 'Nariz'), value: product.tasting.nose, icon: 'ri-bubble-chart-line', color: 'from-[#c9a84c]/20 to-transparent' },
            { label: t('product_palate', 'Paladar'), value: product.tasting.palate, icon: 'ri-drop-line', color: 'from-[#c9a84c]/15 to-transparent' },
            { label: t('product_finish', 'Final'), value: product.tasting.finish, icon: 'ri-time-line', color: 'from-[#c9a84c]/10 to-transparent' },
          ].map(note => (
            <div key={note.label} className="flex gap-4">
              <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-gradient-to-br ${note.color} border border-[#c9a84c]/15`}>
                <i className={`${note.icon} text-[#c9a84c] text-sm`}></i>
              </div>
              <div>
                <p className="text-white/30 text-[10px] tracking-widest uppercase mb-1">{note.label}</p>
                <p className="text-white/65 text-sm font-light leading-relaxed">{note.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Awards */}
      {product.awards && product.awards.length > 0 && (
        <div className="bg-[#111111] border border-white/8 rounded-2xl p-6">
          <p className="text-[#c9a84c] text-[10px] tracking-[0.35em] uppercase mb-4 font-semibold">
            {t('product_awards', 'Prêmios & Reconhecimentos')}
          </p>
          <div className="flex flex-col gap-3">
            {product.awards.map((award, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-medal-line text-[#c9a84c] text-sm"></i>
                </div>
                <p className="text-white/55 text-sm font-light">{award}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductInfo;
